import { useEffect, useRef, useState } from "react";

const areas = [
  {
    icon: "ri-hospital-line",
    title: "Direito Médico",
    desc: "Defesa de profissionais de saúde em processos administrativos, cíveis e penais. Representação em conselhos regionais e ações por erro médico.",
    cta: "Saiba mais",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Direito à Saúde do Paciente",
    desc: "Ações para garantir acesso a tratamentos, medicamentos e cirurgias negados por planos de saúde ou pelo poder público.",
    cta: "Saiba mais",
  },
  {
    icon: "ri-shield-check-line",
    title: "Direito Previdenciário",
    desc: "Aposentadorias, benefícios por incapacidade, revisões do INSS e planejamento previdenciário para uma aposentadoria segura.",
    cta: "Saiba mais",
  },
  {
    icon: "ri-home-heart-line",
    title: "Direito de Família e Sucessão",
    desc: "Divórcio, guarda, pensão alimentícia, inventário, testamento e planejamento patrimonial para proteger sua família.",
    cta: "Saiba mais",
  },
  {
    icon: "ri-briefcase-line",
    title: "Direito Trabalhista",
    desc: "Defesa de direitos em demissões, rescisões, assédio moral, horas extras e reclamações trabalhistas.",
    cta: "Saiba mais",
  },
  {
    icon: "ri-lock-password-line",
    title: "LGPD",
    desc: "Adequação à Lei Geral de Proteção de Dados para empresas e profissionais, e defesa em casos de violação de privacidade.",
    cta: "Saiba mais",
  },
  {
    icon: "ri-file-text-line",
    title: "Direito Civil",
    desc: "Contratos, responsabilidade civil, danos morais e materiais, cobranças e regularização de documentos.",
    cta: "Saiba mais",
  },
  {
    icon: "ri-scales-line",
    title: "Direito Penal",
    desc: "Defesa criminal em todas as instâncias, habeas corpus, revisão criminal e acompanhamento de inquéritos policiais.",
    cta: "Saiba mais",
  },
  {
    icon: "ri-government-line",
    title: "Direito Administrativo",
    desc: "Concursos públicos, processos administrativos, licitações, servidores públicos e relações com o poder público.",
    cta: "Saiba mais",
  },
];

const WHATSAPP_URL = "https://wa.me/5547996077227?text=Olá,%20gostaria%20de%20saber%20mais%20sobre%20";

function AreaCard({ area, hovered, onEnter, onLeave }: {
  area: typeof areas[0];
  hovered: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  return (
    <div
      className="group relative rounded-lg overflow-hidden border border-gray-100 cursor-pointer transition-all duration-400 h-full"
      style={{
        background: hovered ? "linear-gradient(135deg, #0A2472, #1F4ED8)" : "white",
        transition: "background 0.3s ease",
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="p-7 flex flex-col h-full">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-lg mb-5 transition-all duration-300"
          style={{
            background: hovered ? "rgba(255,255,255,0.15)" : "linear-gradient(135deg, #C8A63A15, #E6D18A25)",
          }}
        >
          <i
            className={`${area.icon} text-2xl transition-colors duration-300`}
            style={{ color: hovered ? "#E6D18A" : "#C8A63A" }}
          />
        </div>
        <h4
          className="font-bold mb-3 text-base transition-colors duration-300"
          style={{ fontFamily: "'Inter', sans-serif", color: hovered ? "white" : "#0A2472" }}
        >
          {area.title}
        </h4>
        <p
          className="text-xs leading-relaxed mb-5 flex-1 transition-colors duration-300"
          style={{ fontFamily: "'Inter', sans-serif", color: hovered ? "rgba(255,255,255,0.75)" : "#6b7280" }}
        >
          {area.desc}
        </p>
        <a
          href={`${WHATSAPP_URL}${encodeURIComponent(area.title)}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="inline-flex items-center gap-2 text-xs font-semibold transition-all duration-300 cursor-pointer"
          style={{ color: hovered ? "#E6D18A" : "#1F4ED8", fontFamily: "'Inter', sans-serif" }}
        >
          {area.cta}
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line" />
          </span>
        </a>
      </div>
      <div
        className="absolute bottom-0 left-0 h-0.5 transition-all duration-500"
        style={{
          width: hovered ? "100%" : "0%",
          background: "linear-gradient(90deg, #C8A63A, #E6D18A)",
        }}
      />
    </div>
  );
}

export default function AreasAtuacao() {
  const sectionRef = useRef<HTMLElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".area-card").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handleScroll = () => {
      const cardWidth = el.scrollWidth / areas.length;
      const idx = Math.round(el.scrollLeft / cardWidth);
      setActiveSlide(idx);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (idx: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / areas.length;
    el.scrollTo({ left: cardWidth * idx, behavior: "smooth" });
    setActiveSlide(idx);
  };

  return (
    <section id="areas" ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-10 h-px bg-[#C8A63A]" />
            <span
              className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C8A63A]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Especialidades
            </span>
            <div className="w-10 h-px bg-[#C8A63A]" />
          </div>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0A2472] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Áreas de Atuação
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
            Atuação completa e especializada para proteger seus direitos em todas as frentes.
          </p>
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden">
          <div
            ref={carouselRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" } as React.CSSProperties}
          >
            {areas.map((area) => (
              <div
                key={area.title}
                className="snap-start flex-shrink-0"
                style={{ width: "80vw", maxWidth: "320px" }}
              >
                <AreaCard
                  area={area}
                  hovered={false}
                  onEnter={() => {}}
                  onLeave={() => {}}
                />
              </div>
            ))}
          </div>
          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-5">
            {areas.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                className="rounded-full transition-all duration-300 cursor-pointer"
                style={{
                  width: activeSlide === i ? "20px" : "8px",
                  height: "8px",
                  background: activeSlide === i ? "#C8A63A" : "#0A2472",
                  opacity: activeSlide === i ? 1 : 0.3,
                }}
              />
            ))}
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {areas.map((area, i) => (
            <div
              key={area.title}
              className="area-card"
              style={{ opacity: 0, transform: "translateY(25px)", transition: `opacity 0.6s ease, transform 0.6s ease` }}
            >
              <AreaCard
                area={area}
                hovered={hoveredIndex === i}
                onEnter={() => setHoveredIndex(i)}
                onLeave={() => setHoveredIndex(null)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
